// frontend/src/features/citysense/housing-stock-modeller/utils/optimizer.ts

import { HousingPolygon, OptimizerResult } from '../types';
import { RETROFIT_DATA, extractAgeClass } from '../constants/housingConfig';
import { computeSummary } from './calculator';

export function runHousingOptimizer(
  polygons: HousingPolygon[],
  baseArchetype: string, // Source archetype to upgrade
  targetArchetype: string, // Target archetype (e.g., "Post-2010")
  minDeprivationIndex: string // Minimum DI level for affordability
): OptimizerResult {
  const deprivationOrder = [
    'Very Disadvantaged',
    'Disadvantaged',
    'Marginally Below Average',
    'Marginally Above Average',
    'Affluent',
    'Very Affluent',
  ];

  const minDIIndex = deprivationOrder.indexOf(minDeprivationIndex);

  // Find polygons that match criteria
  const eligible = polygons.filter((p) => {
    const matchesArchetype = p.archetype === baseArchetype;
    const diIndex = deprivationOrder.indexOf(p.deprivationIndex);
    const meetsAffordability = diIndex >= minDIIndex;
    return matchesArchetype && meetsAffordability;
  });

  const upgradedIds = new Set(eligible.map((p) => p.id));

  // Create upgraded polygons
  const upgradedPolygons = polygons.map((p) =>
    upgradedIds.has(p.id) ? { ...p, archetype: targetArchetype } : p
  );

  // Calculate costs and savings
  let totalCost = 0;
  let totalGHGSavings = 0;
  let dwellingsAffected = 0;

  eligible.forEach((p) => {
    const baseAge = extractAgeClass(baseArchetype);
    const targetAge = extractAgeClass(targetArchetype);
    
    const baseCost = RETROFIT_DATA[baseAge];
    const targetCost = RETROFIT_DATA[targetAge];
    
    // Cost difference (assume we're upgrading to better)
    const costDiff = baseCost.cost;
    const ghgDiff = baseCost.twentyYear - targetCost.twentyYear;

    totalCost += costDiff * p.dwellings;
    totalGHGSavings += ghgDiff * p.dwellings;
    dwellingsAffected += p.dwellings;
  });

  const before = computeSummary(polygons);
  const after = computeSummary(upgradedPolygons);

  return {
    upgradedPolygons: upgradedIds,
    totalCost,
    totalGHGSavings,
    dwellingsAffected,
    before,
    after,
  };
}